<template>
  <v-row justify="center">
    <v-dialog
      v-model="open"
      @click:outside="handleClose"
      width="565px"
      scrollable
      :fullscreen="isMobileBreakpoint"
      :persistent="isMobileBreakpoint"
    >
      <v-card>
        <v-row no-gutters class="px-6 py-7 align-start" style="flex: 0 1 auto">
          <v-col cols="10">
            <span class="body-bold-medium neutral-20-dark--text d-block">
              {{ $t('message.pages.drmModalDashboard.title') }}
            </span>
            <span class="body-regular-smaller neutral-40-light--text d-block">
              {{ $t('message.pages.drmModalDashboard.subtitle') }}
            </span>
          </v-col>
          <v-col cols="2" class="d-flex flex-row align-center justify-end">
            <v-icon style="cursor: pointer" @click="handleClose()">
              mdi-close
            </v-icon>
          </v-col>
        </v-row>

        <v-card-text
          style="height: 310px"
          class="pa-0 pl-5"
          :class="drmGroups.length > 3 ? 'pr-2' : 'pr-6'"
        >
          <v-row no-gutters>
            <BaseAlert
              v-if="!alreadyDrm && !isFolder"
              type="informative"
              style="width: 98%; margin-bottom: 12px"
              :title="$t('message.pages.drmModalDashboard.baseAlert.title')"
              :description="
                $t('message.pages.drmModalDashboard.baseAlert.description')
              "
            ></BaseAlert>
            <BaseAlert
              v-else-if="inFolder && alreadyDrm"
              type="warning"
              style="width: 98%; margin-bottom: 12px"
              :title="
                $t('message.pages.drmModalDashboard.baseAlertAlreadyDrm.title')
              "
              :description="
                $t(
                  'message.pages.drmModalDashboard.baseAlertAlreadyDrm.description'
                )
              "
            ></BaseAlert>
            <v-col
              cols="12"
              v-for="(group, i) in drmGroups"
              :key="i"
              class="d-flex flex-column py-3 pl-1"
              :class="{ 'bordered-group': i < drmGroups.length - 1 }"
            >
              <v-tooltip bottom :disabled="!alreadyDrm">
                <template v-slot:activator="{ on, attrs }">
                  <v-row
                    v-bind="attrs"
                    v-on="on"
                    no-gutters
                    align="center"
                    class="d-flex justify-space-between"
                    style="width: 100%"
                  >
                    <div
                      class="d-flex align-center"
                      style="min-width: 0; flex: 1 1 auto"
                    >
                      <v-card
                        class="d-flex align-center justify-center mr-2"
                        flat
                        color="background"
                      >
                        <v-btn
                          :disabled="alreadyDrm"
                          icon
                          @click="handleActiveGroup(group)"
                        >
                          <v-card
                            class="d-flex align-center justify-center elevation-0 rounded-pill"
                            width="20px"
                            height="20px"
                            :class="{
                              'neutral-95-lightest':
                                alreadyDrm && group.isActive,
                            }"
                            :color="
                              group.isActive
                                ? '#3F55EE'
                                : $vuetify.theme.dark
                                ? '#000000'
                                : '#FFFFFF'
                            "
                            :style="
                              group.isActive
                                ? ''
                                : $vuetify.theme.dark
                                ? 'border: 2px solid #52577A'
                                : 'border: 2px solid #D0D1D9'
                            "
                          >
                            <v-card
                              v-if="group.isActive"
                              :class="{
                                'neutral-80-lighter':
                                  alreadyDrm && group.isActive,
                              }"
                              :color="
                                group.isActive
                                  ? '#FFFFFF'
                                  : !$vuetify.theme.dark
                                  ? '#000000'
                                  : '#FFFFFF'
                              "
                              width="10px"
                              height="10px"
                              flat
                              class="rounded-pill"
                            />
                          </v-card>
                        </v-btn>
                      </v-card>

                      <span
                        :title="group.name"
                        class="text-truncate body-regular-small"
                        :class="{
                          'neutral-30-base--text': group.isActive,
                          'neutral-40-light--text': !group.isActive,
                        }"
                        style="
                          display: block;
                          overflow: hidden;
                          white-space: nowrap;
                          text-overflow: ellipsis;
                        "
                      >
                        {{ group.name }}
                      </span>
                    </div>

                    <div
                      class="ml-2"
                      :style="{
                        width: maxTagWidth + 'px',
                        display: 'flex',
                        justifyContent: 'flex-start',
                      }"
                    >
                      <v-tag
                        v-if="group.webhook_source_name"
                        type="neutral"
                        class="mx-0"
                        style="
                          white-space: nowrap;
                          overflow: hidden;
                          text-overflow: ellipsis;
                        "
                      >
                        {{ formatTagName(group.webhook_source_name) }}
                      </v-tag>
                    </div>
                  </v-row>
                </template>
                <span>{{
                  $t('message.pages.drmModalDashboard.tooltipAlreadyDrm')
                }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-7">
          <v-spacer v-if="!isMobileBreakpoint"></v-spacer>
          <base-btn
            type="tertiary"
            class="mr-3"
            @click="handleClose"
            :disabled="isFetching"
          >
            <span>{{ $t('message.cancel') }}</span>
          </base-btn>
          <v-tooltip top :disabled="!confirmTooltipMessage">
            <template v-slot:activator="{ on, attrs }">
              <div
                class="btn-tooltip"
                v-bind="attrs"
                v-on="confirmTooltipMessage ? on : {}"
                style="display: inline-block"
              >
                <base-btn
                  :loading="isFetching"
                  :disabled="!canConfirm"
                  @click="handleConfirm"
                >
                  <span>{{ $t('message.confirm') }}</span>
                </base-btn>
              </div>
            </template>
            <span style="white-space: pre-line">
              {{ confirmTooltipMessage }}
            </span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import drmApi from '@/services/drmApi'
import { mapMutations } from 'vuex/dist/vuex.common.js'
import { mapState } from 'vuex'
import BaseAlert from '@/components/BaseAlert.vue'
export default {
  name: 'DrmModalDashboard',
  components: { BaseAlert },
  data: () => ({
    open: null,
    isFetching: false,
    drmGroups: [],
    startValueDrmGroups: [],
    selectedGroups: [],
    selectedItem: null,
    initialActiveItem: null,
    maxTagWidth: 110,
    alreadyDrm: false,
    inFolder: false,
    isFolder: false,
  }),
  computed: {
    ...mapState('user', {
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
    canConfirm() {
      const currentActive = this.drmGroups.find((g) => g.isActive)?.id
      const initialActive = this.initialActiveItem?.id
      return !this.isFetching && currentActive !== initialActive
    },
    confirmTooltipMessage() {
      const currentActive = this.drmGroups.find((g) => g.isActive)?.id
      const initialActive = this.initialActiveItem?.id

      const noChange = currentActive === initialActive

      if (!this.isFetching && noChange) {
        return this.$t('message.noChanges')
      }

      return ''
    },
  },
  watch: {
    open(val) {
      if (val === false) this.$root.$emit('DrmModalHasClosed')
    },
  },
  mounted() {
    this.$root.$on('finishUpdateFolderVideosPage', () => {
      this.isFetching = false
      this.handleClose()
    })
    this.$root.$on('drmModalDashboard', (data) => {
      let parent_folderId = localStorage.getItem('folderFather')

      if (!parent_folderId && this.$route.params.id)
        parent_folderId = this.$route.params.id

      if (parent_folderId && this.$route.params.id) {
        const drms_groups = data.groups.flatMap((row) => row.folder_ids)
        this.alreadyDrm = drms_groups.some((row) => row == parent_folderId)
        this.inFolder = !!parent_folderId
      }
      this.isFolder = !!data.item.title
      Object.assign(this.$data, this.initialState())
      this.setInfo(data.groups, data.item)
      this.open = true
      this.calculateMaxTagWidth()
    })
  },
  beforeDestroy() {
    this.$root.$off('finishUpdateFolderVideosPage')
    this.$root.$off('drmModalDashboard')
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    formatTagName(name) {
      return name
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    },
    calculateMaxTagWidth() {
      this.$nextTick(() => {
        const tags = this.drmGroups
          .filter((g) => g.webhook_source_name)
          .map((g) => this.formatTagName(g.webhook_source_name))

        if (tags.length === 0) {
          this.maxTagWidth = 0
          return
        }

        const longestTag = tags.reduce((a, b) => (a.length > b.length ? a : b))

        this.maxTagWidth = Math.min(
          Math.max(longestTag.length * 8 + 20, 70),
          200
        )
      })
    },
    handleActiveGroup(activateGroup) {
      if (activateGroup.isActive) return

      this.drmGroups = this.drmGroups.map((group, index) => {
        if (
          index === 0 &&
          group.value === 'none' &&
          group.id === activateGroup.id
        ) {
          return { ...group, isActive: true }
        } else if (group.id === activateGroup.id) {
          this.drmGroups[0].isActive = false
          return { ...group, isActive: true }
        } else if (group.isActive) {
          return { ...group, isActive: false }
        }
        return group
      })
    },
    setInfo(groups, item) {
      this.selectedItem = item
      let fixedGroups = []

      if (this.selectedItem.drm_group_ids) {
        fixedGroups = groups.map((group) => {
          let foundGroup = this.selectedItem.drm_group_ids.find(
            (el) => el.drm_group_id === group.id
          )
          return {
            ...group,
            isActive: !!foundGroup,
            webhook_source_name: group.webhook_source_name || 'api',
          }
        })
      } else {
        fixedGroups = groups.map((group) => ({
          ...group,
          isActive: false,
          webhook_source_name: group.webhook_source_name || 'api',
        }))
      }

      const noneOption = {
        name: this.$i18next.t('message.pages.drmModalDashboard.none'),
        value: 'none',
        isActive: !this.selectedItem.drm_group_ids,
      }

      this.startValueDrmGroups = [
        noneOption,
        ...JSON.parse(JSON.stringify(fixedGroups)),
      ]
      this.drmGroups = [noneOption, ...JSON.parse(JSON.stringify(fixedGroups))]

      this.initialActiveItem = this.drmGroups.find((item) => item.isActive)
    },
    updateSnackbar() {
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: this.$i18next.t('message.pages.drmModalDashboard.updateSuccess'),
        type: 'success',
      })
    },
    async handleConfirm() {
      this.isFetching = true
      this.getNewGroups()
    },
    async removeFromAllGroups() {
      let modifiedData = null
      if (this.selectedItem.name) {
        modifiedData = {
          folders: {
            action: 'delete',
            folder_ids: [this.selectedItem.id],
          },
        }
      } else {
        modifiedData = {
          videos: {
            action: 'delete',
            video_ids: [this.selectedItem.id],
          },
        }
      }
      let removingIds = this.selectedItem.drm_group_ids.map(
        (item) => item.drm_group_id
      )
      for (let removingId of removingIds) {
        try {
          await drmApi.drmUpdateVideos(removingId, modifiedData)
          this.selectedItem.drm_group_ids =
            this.selectedItem.drm_group_ids.filter(
              (item) => item.drm_group_id != removingId
            )
          if (!this.selectedItem.drm_group_ids.length)
            delete this.selectedItem['drm_group_ids']
        } catch (e) {
          console.log('e', e)
        }
      }
      this.updateSnackbar()
      this.handleClose()
    },
    async getNewGroups() {
      let noGroup = this.drmGroups[0]

      if (noGroup.isActive) {
        if (this.initialActiveItem?.value === 'none') {
          this.updateSnackbar()
          this.handleClose()
          return
        }
        await this.removeFromAllGroups()
        this.updateInPage()
        return
      }

      let startActiveArray = this.startValueDrmGroups
        .slice(1)
        .map((group) => group.isActive)
      let newActiveArray = this.drmGroups
        .slice(1)
        .map((group) => group.isActive)

      let arrayIndexes = []
      for (let i = 0; i < startActiveArray.length; i++) {
        if (startActiveArray[i] !== newActiveArray[i]) {
          arrayIndexes.push(i + 1)
        }
      }
      this.getGroupChanges(arrayIndexes)
    },
    async getGroupChanges(indexes) {
      let toBeAddedGroups = []
      let toBeRemovedGroups = []
      for (let index of indexes) {
        let changedGroup = this.drmGroups[index]
        if (this.selectedItem.drm_group_ids) {
          let findGroup = this.selectedItem.drm_group_ids.find(
            (group) => group.drm_group_id === changedGroup.id
          )
          if (findGroup) {
            toBeRemovedGroups.push(changedGroup)
          } else {
            toBeAddedGroups.push(changedGroup)
          }
        } else {
          toBeAddedGroups.push(changedGroup)
        }
      }
      for (let group of toBeRemovedGroups) {
        await this.removeItemFromGroup(group)
      }
      for (let group of toBeAddedGroups) {
        await this.addItemFromGroup(group)
      }
      this.updateInPage()
      if (!this.selectedItem.name) {
        this.isFetching = false
        this.updateSnackbar()
        this.handleClose()
      }
    },
    async addItemFromGroup(group) {
      let modifiedData = null
      if (this.selectedItem.name) {
        modifiedData = {
          folders: {
            action: 'increment',
            folder_ids: [this.selectedItem.id],
          },
        }
      } else {
        modifiedData = {
          videos: {
            action: 'increment',
            video_ids: [this.selectedItem.id],
          },
        }
      }
      try {
        await drmApi.drmUpdateVideos(group.id, modifiedData)
        let newObj = {
          active: group.active,
          drm_group_id: group.id,
        }
        if (this.selectedItem.drm_group_ids) {
          this.selectedItem.drm_group_ids.push(newObj)
        } else {
          this.selectedItem.drm_group_ids = [newObj]
        }
      } catch (e) {
        console.log('e', e)
      }
    },
    async removeItemFromGroup(group) {
      let modifiedData = null
      if (this.selectedItem.name) {
        modifiedData = {
          folders: {
            action: 'delete',
            folder_ids: [this.selectedItem.id],
          },
        }
      } else {
        modifiedData = {
          videos: {
            action: 'delete',
            video_ids: [this.selectedItem.id],
          },
        }
      }
      try {
        await drmApi.drmUpdateVideos(group.id, modifiedData)
        this.selectedItem.drm_group_ids =
          this.selectedItem.drm_group_ids.filter(
            (item) => item.drm_group_id != group.id
          )
        if (!this.selectedItem.drm_group_ids.length)
          delete this.selectedItem['drm_group_ids']
      } catch (e) {
        console.log('e', e)
      }
    },
    updateInPage() {
      if (this.selectedItem.name) {
        this.$root.$emit('updateFolderVideosPage', this.selectedItem)
      } else {
        this.$root.$emit('updateVideoVideosPage', this.selectedItem)
      }
    },
    handleClose() {
      this.open = null
      this.alreadyDrm = false
      this.$root.$emit('resetIsAnyOtherModal')
    },
    initialState() {
      return {
        open: null,
        isFetching: false,
        drmGroups: [],
        startValueDrmGroups: [],
        selectedGroups: [],
        selectedItem: null,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.disabled-border {
}
.bordered-group {
  border-bottom: 1px solid varFigma(neutral-95-lightest);
}

@media (max-width: 768px) {
  ::v-deep .v-card__actions {
    justify-content: space-between;
    padding: 16px 20px !important;

    .base-button {
      width: 50%;
    }

    .btn-tooltip {
      width: 50%;

      .base-button {
        width: 100%;
      }
    }
  }
}
</style>
